import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import { type CalendarFocusMonth, clampCalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import { ANYWHERE_PLACE_ID, DATE_SEARCH_OPTION_IDS } from 'constants/search'
import type { RefObject } from 'react'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { fetchExperienceListFacets } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addDays, addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import moment, { type Moment } from 'moment'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import ExperiencesSearchDateQuickChips, {
  CHIP_ANYTIME,
  type ExperiencesDateQuickChipSelectPayload,
} from './ExperiencesSearchDateQuickChips'
import styled from 'styled-components'

const CalendarWrapper = styled.div`
  width: 100%;

  .vertical {
    flex-direction: column;

    > * {
      flex-grow: 1;

      & + * {
        margin-inline-start: 0;
      }
    }
  }
`

interface Props {
  calendarDirection?: 'vertical' | 'horizontal';
  quickChipsDisplayType?: 'single-line' | 'multi-line';
  calendarScrollerRef?: RefObject<HTMLDivElement | null>;
  tripDateRange?: { start: Moment, end: Moment };
  showNextBooking?: boolean;
  /**
   * Month the calendar should open on when no date has been picked yet.
   * Only moves the viewport — it never selects a date.
   */
  focusMonth?: CalendarFocusMonth;
}

function ExperiencesSearchDateSelectInline(props: Props) {
  const { calendarDirection = 'horizontal', quickChipsDisplayType, calendarScrollerRef, tripDateRange, showNextBooking = true, focusMonth } = props

  const { checkinDate, checkoutDate, dateSearchOptionId, searchItem } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const dispatch = useAppDispatch()

  const effectivePlaceId = useMemo(
    () => searchItem?.searchType === 'destination' ? searchItem.value : ANYWHERE_PLACE_ID,
    [searchItem?.searchType, searchItem?.value],
  )

  useEffect(() => {
    dispatch(fetchExperienceListFacets(effectivePlaceId))
  }, [effectivePlaceId, dispatch])

  const dateRange = useAppSelector(state => state.experience.experienceListFacets[effectivePlaceId]?.facets?.dateRange)

  const today = useMemo(() => beginningOfToday(), [])

  const minDate = useMemo(() => {
    if (dateRange?.min) {
      const apiMin = moment(dateRange.min).toDate()
      return apiMin > today ? apiMin : today
    }
    return today
  }, [dateRange, today])

  const maxDate = useMemo(() => {
    if (dateRange?.max) {
      return moment(dateRange.max).toDate()
    }
    if (checkinDate && !checkoutDate) {
      return addDays(checkinDate.toDate(), 31)
    }
    return addMonths(today, 36)
  }, [checkinDate, checkoutDate, dateRange, today])

  // An existing check-in date already drives the calendar, so the hint only
  // applies before the traveller has picked anything.
  const calendarFocusDate = useMemo(
    () => checkinDate ? undefined : clampCalendarFocusMonth(focusMonth, minDate, maxDate),
    [checkinDate, focusMonth, maxDate, minDate],
  )

  const unsetDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
  }, [globalSearchDispatch])

  const unsetFlexibleDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_DURATION })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_FLEXIBLE_MONTH_RANGE })
    globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS })
  }, [globalSearchDispatch])

  const selectSpecificDates = useCallback(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: false })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })
    unsetFlexibleDates()
  }, [globalSearchDispatch, unsetFlexibleDates])

  const selectAnytime = useCallback(() => {
    unsetDates()
    unsetFlexibleDates()
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.ANYTIME })
    globalSearchDispatch({ type: GlobalSearchStateActions.TOGGLE_ANYTIME_DATES_SELECTED, selected: true })
  }, [globalSearchDispatch, unsetDates, unsetFlexibleDates])

  const handleDatesChange = useCallback(
    (dates: { startDate: Moment, endDate: Moment | undefined }) => {
      const hasSingleSelectedDate = checkinDate && (!checkoutDate || checkoutDate.isSame(checkinDate, 'day'))
      const isDeselectingSingleDate = hasSingleSelectedDate &&
        dates.startDate.isSame(checkinDate, 'day') &&
        (!dates.endDate || dates.endDate.isSame(checkinDate, 'day'))

      if (isDeselectingSingleDate) {
        selectAnytime()
        return
      }

      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKIN_DATE,
        date: dates.startDate,
      })
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
        date: dates.endDate,
      })
      if (dates.startDate) {
        selectSpecificDates()
      }
    },
    [checkinDate, checkoutDate, globalSearchDispatch, selectAnytime, selectSpecificDates],
  )

  const selectDayRange = useCallback((start: Moment, end: Moment) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: start })
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: end })
    selectSpecificDates()
  }, [globalSearchDispatch, selectSpecificDates])

  const onQuickChipSelect = useCallback(
    (payload: ExperiencesDateQuickChipSelectPayload) => {
      if (payload.value === CHIP_ANYTIME || !payload.range) {
        selectAnytime()
        return
      }
      selectDayRange(payload.range.start, payload.range.end)
    },
    [selectAnytime, selectDayRange],
  )

  return (
    <>
      <ExperiencesSearchDateQuickChips
        checkinDate={checkinDate}
        checkoutDate={checkoutDate}
        dateSearchOptionId={dateSearchOptionId}
        onSelect={onQuickChipSelect}
        displayType={quickChipsDisplayType}
        tripDateRange={tripDateRange}
        showNextBooking={showNextBooking && !tripDateRange}
        minDate={minDate}
        maxDate={maxDate}
      />
      <CalendarWrapper>
        {calendarDirection === 'vertical' && <VerticalDateRangePicker
          startDate={checkinDate}
          endDate={checkoutDate}
          min={minDate}
          max={maxDate}
          onDatesChange={handleDatesChange}
          allowSameDayRange
          contentRef={calendarScrollerRef}
          focusMonth={calendarFocusDate}
        />}
        {calendarDirection !== 'vertical' && <DualDateRangePicker
          onDatesChange={handleDatesChange}
          startDate={checkinDate}
          endDate={checkoutDate}
          minDate={minDate}
          maxDate={maxDate}
          allowSameDayRange
          className={calendarDirection}
          focusMonth={calendarFocusDate}
        />}
      </CalendarWrapper>
    </>
  )
}

export default ExperiencesSearchDateSelectInline
